import React, { useState, useEffect, useRef } from 'react'
import Moment from 'moment'
import 'react-dates/initialize';
import { SingleDatePicker } from 'react-dates';
import 'react-dates/lib/css/_datepicker.css';
import { ask } from 'shared/concierge'

Moment.locale('en-gb');

// onFacets / onEnquiry / currentPath are supplied only by the product grid.
// With them, a concierge answer (or a pill edit) for the page you are already
// on refines it in place; without them (homepage, occasion landing pages) it
// navigates to the results page.
//
// labels / ctaLabel / modal / cityChips / vibes / askPlaceholder are the
// category-modal variant (design handoff 6a/6b): labelled inputs, a specific
// CTA ("Search hen parties"), popular-city shortcuts and query-starter chips
// in AI mode. config.sections / config.activities add the homepage's party-
// or-activity-type select.
const EnquiryForm = ({ config, type, hideLocation, onFacets, onEnquiry, onProximity, currentPath, visibleProductIds, labels, ctaLabel, modal, cityChips, vibes, askPlaceholder }) => {
    const parseDate = (date) => {

        // The session can hold '' or junk after a cleared search; an invalid
        // Moment still formats, as "NaN undefined -0NaN" in a chip.
        if( date == null || !/^\d{1,2}\/\d{1,2}\/\d{4}$/.test( date ) ) {
            return null;
        }
        let objDate = Moment();
        const dateParts = date.split('/');
        objDate.set('year', Number(dateParts[2]) );
        objDate.set('month', Number(dateParts[1])-1 );
        objDate.set('date', Number(dateParts[0]) );
        objDate.set('hour', 4 );
        objDate.set('minute', 0);
        objDate.set('second', 0 );
        objDate.set('millisecond', 0);

        return objDate;
    }

    // The homepage band carries a type select; the choice decides which cities
    // are offered and where the form POSTs. No selection means "everything" —
    // the city landing page.
    //
    // Party types (config.sections) are the FIRST path segment —
    // /hen-parties/london — and each carries its own city list. Activity types
    // (config.activities) are the LAST — /london/escape-rooms.
    // ponytail: activity types reuse the full city list rather than shipping a
    // city list each; sub_categories#location already redirects a city with
    // none of that activity to the city page.
    const sections = config.sections || []
    const activities = config.activities || []
    const [ searchType, setSearchType ] = useState( '' )

    // 'p:<section-slug>' / 'a:<activity-slug>' — the two kinds can share a slug
    // and build opposite paths, so the kind travels with the value.
    const resolveType = ( value ) => {
        const [ kind, slug ] = value.split(':')
        if( kind === 'a' ) {
            const activity = activities.find( a => a.slug === slug )
            return activity ? { path: '/*/' + slug, cities: config } : null
        }
        const section = sections.find( s => s.slug === slug )
        return section ? { path: '/' + slug + '/*', cities: section } : null
    }

    const activeType = resolveType( searchType )
    const fieldConfig = activeType
        ? { ...config, locations: activeType.cities.locations, hasRegion: activeType.cities.hasRegion }
        : config

    const genPath = ( location ) => {
        const path = activeType ? activeType.path : config.path
        return path.replace('*', location)
    }

    const [ location, setLocation ] = useState( config.defaultValues &&  config.defaultValues.location != undefined ? config.defaultValues.location : '' )
    const [ date, setDate ] = useState( config.defaultValues ? parseDate( config.defaultValues.date ) : null )
    const [ pax, setPax ] = useState( config.defaultValues ? config.defaultValues.pax : '' )
    //const [ url, setUrl ] = useState( config.defaultValues &&  config.defaultValues.location != undefined ? '/' + config.prefix + '/' + config.defaultValues.location : '/' + config.prefix )
    const [ url, setUrl ] = useState( genPath( config.defaultValues && config.defaultValues.location != undefined ? config.defaultValues.location : null ) )
    const [ actioned, setActioned ] = useState( false )
    const formRef = useRef()  
    
    const [ formData, setFormData ] = useState({
    	pax: config.defaultValues ? config.defaultValues.pax : '',
    	date: config.defaultValues ? parseDate( config.defaultValues.date ) : null,
    	location: config.defaultValues &&  config.defaultValues.location != undefined ? config.defaultValues.location : '',
    	actioned: false
    })
    

    
    const redirect = () => {
        history.pushState(undefined, 'Home', url);
        let url = '/' + location;
        if( config.prefix != undefined && config.prefix != null && config.prefix != '' ) {
            url = '/' + config.prefix + '/' + location;
        }
        window.location.replace(url);
    }
    
    
    // Quick search auto-submits once all three fields are set. The concierge
    // pills auto-apply too: answering "+ How many people?" should just refine
    // the results. On the grid that goes through onEnquiry — /products/list2
    // writes session[:enquiry] and refetches in place, so the page doesn't
    // reload and jump to the top. Elsewhere it falls back to a real submit,
    // which is safe because the form action carries ?q=. actioned
    // distinguishes a guest edit from a model answer (applyToFields), which
    // must never fire a search mid-conversation.
    useEffect(() => {
    	if( !formData.actioned ) return
    	if( concierge ) {
    		if( onEnquiry ) {
    			onEnquiry({
    				pax:  formData.pax || '',
    				date: formData.date && formData.date.isValid() ? formData.date.format('DD/MM/YYYY') : ''
    			})
    		} else if( canSubmit() ) {
    			formRef.current.requestSubmit()
    		}
    	} else if( config.autoSubmit && isFormComplete() ) {
        	formRef.current.requestSubmit()
        }

	}, [formData])
    
    
    // Used only to decide whether to auto-submit in quick search.
    const isFormComplete = () => {
    	return ( formData.pax != '' && formData.pax > 0 ) && ( formData.location != '' && formData.location.length > 0 ) && ( formData.date != null ) ? true : false
    }

    // What Search needs, which is much less. Group size and date are optional
    // filters — searching a city with neither, or with only one, is a perfectly
    // good search. A city is the one hard requirement, because the form POSTs to
    // its path: without one the action is "/group-activities/" and goes nowhere.
    const canSubmit = () => {
    	return formData.location != undefined && formData.location != null && formData.location.length > 0
    }

    const flatLocations = ( cfg ) =>
        cfg.hasRegion ? [ ...cfg.locations.uk, ...cfg.locations.eu ] : cfg.locations

    const chooseSearchType = ( value ) => {
        setSearchType( value )
        const type = resolveType( value )
        // A city picked under the old type may not be served by the new one —
        // keep it if it is, clear it if not.
        const keep = flatLocations( type ? type.cities : config ).some( l => l.slug === formData.location )
        const nextLocation = keep ? formData.location : ''
        const path = type ? type.path : config.path
        setLocation( nextLocation )
        setFormData({ ...formData, location: nextLocation, actioned: false })
        setUrl( path.replace('*', nextLocation || null) )
    }

    // The modal's popular-city shortcuts: a fixed shortlist per the design,
    // shown only where this occasion actually serves the city.
    const CHIP_CITIES = [ 'london', 'manchester', 'liverpool', 'brighton' ]
    const chipList = () => {
        if( !cityChips ) return []
        const list = fieldConfig.hasRegion ? fieldConfig.locations.uk : fieldConfig.locations
        return list.filter( l => CHIP_CITIES.indexOf( l.slug ) !== -1 )
    }

    const pickCity = ( slug ) => {
        setLocation( slug )
        setUrl( genPath( slug ) )
        setFormData({ ...formData, location: slug, actioned: false })
    }

    ////////////////////
    //   CONCIERGE    //
    ////////////////////

    // Doubles as the quick-search nudge and the AI-mode placeholder, per the
    // design. The occasion comes from the section slug in the path and the
    // city from the form's location — which the grid presets to the page's
    // own city — so the Leeds page never suggests a party in Manchester.
    // Section roots (no city yet) fall back to a fixed example city, and an
    // explicit askPlaceholder prop still overrides the placeholder entirely.
    const exampleCity = ( formData.location || '' ).replace( /-/g, ' ' )
    const EXAMPLE_QUERIES = {
        'hen-parties':       `hen party ideas in ${ exampleCity || 'york' } in june`,
        'stag-do-ideas':     `stag do ideas in ${ exampleCity || 'prague' } in may`,
        'team-building':     `team building day in ${ exampleCity || 'manchester' } for 20 people`,
        'birthday-parties':  `30th birthday ideas in ${ exampleCity || 'leeds' } in august`,
        'group-activities':  `group activities in ${ exampleCity || 'bristol' } for 10 people`,
        'christmas-parties': `christmas party in ${ exampleCity || 'london' } for 15 people`
    }
    const pagePath = currentPath || ( config.path || '' )
    const sectionKey = Object.keys( EXAMPLE_QUERIES ).find( slug => pagePath.indexOf( slug ) !== -1 )
    // City+category pages (/london/party-boats) carry no section slug, so key
    // the example on the category segment instead — a party-boat page selling
    // stag dos reads as broken. The city segment and the path's '*'
    // placeholder are dropped; city-only pages fall through to the default.
    const categorySlug = sectionKey ? null : pagePath.split( '/' )
        .filter( seg => seg && seg !== '*' && seg !== formData.location )
        .pop()
    const EXAMPLE_QUERY = sectionKey ? EXAMPLE_QUERIES[ sectionKey ]
        : categorySlug ? `${ categorySlug.replace( /-/g, ' ' ) } in ${ exampleCity || 'london' } in may`
        : EXAMPLE_QUERIES['stag-do-ideas']

    // Query starters for the category modal's AI mode, per the design.
    const VIBES = [ 'Chilled weekend', 'Big night out', 'Something different', 'On a budget' ]
    const addVibe = ( vibe ) => {
        setQuery( q => q.trim() ? q.trim() + ', ' + vibe.toLowerCase() : vibe )
    }

    const FORM_ID = 'enquiry-form'

    const [ concierge, setConcierge ] = useState( false )
    const [ query, setQuery ] = useState( '' )
    const [ asking, setAsking ] = useState( false )
    const [ reply, setReply ] = useState( null )
    const [ error, setError ] = useState( null )

    // Every page opens on quick search. The mode is deliberately NOT remembered:
    // choosing the concierge once shouldn't silently change what the next landing
    // page looks like, and a restored mode brought session chips back with it, so
    // the band appeared mid-search before the guest had typed anything.
    //
    // ?q= is the one exception — that is someone opening a shared concierge search,
    // which is explicit intent. Read after mount so prerendered markup stays
    // cacheable and identical for everyone.
    useEffect(() => {
        const q = new URLSearchParams( window.location.search ).get('q')
        if( q ) {
            setConcierge( true )
            setQuery( q )
        }
    }, [])

    const chooseMode = ( wantConcierge ) => {
        setConcierge( wantConcierge )
        setError( null )
    }

    // Whatever the concierge understood is written back into the three fields so
    // the guest can see it and correct it. actioned stays false on purpose:
    // autoSubmit would otherwise POST the form and force a page load, defeating
    // the in-place refine.
    const applyToFields = ( filters ) => {
        setFormData( previous => ({
            ...previous,
            pax:      filters.pax != null ? String( filters.pax ) : previous.pax,
            date:     filters.date ? Moment( filters.date, 'YYYY-MM-DD' ) : previous.date,
            location: filters.location || previous.location,
            actioned: false
        }))

        // Keep the form's own POST target in step, or the Search button would
        // submit to the previous city.
        if( filters.location ) {
            setLocation( filters.location )
            setUrl( genPath( filters.location ) )
        }
    }

    const askConcierge = () => {

        const text = query.trim()
        if( !text || asking ) return

        setAsking( true )
        setReply( null )
        setError( null )

        ask( config.token, text, currentPath, visibleProductIds ).then( data => {

            applyToFields( data.filters || {} )
            setReply( data.reply )
            setAsking( false )

            // A distance question is answered about what's on screen, so the grid
            // opens its map and lights up the activities that were measured. A
            // "near somewhere" question also narrows the grid to that area.
            if( onProximity && ( ( data.compare && data.compare.length ) || data.near ) ) {
                onProximity( data.compare || [], data.near || null )
            }

            // Same page: refine without a reload. Different occasion or city is a
            // different results page, and those are the SEO URLs — navigate.
            if( onFacets && data.path && data.path === currentPath ) {
                onFacets( data.facets || {} )
                rememberQuery( text )
            } else if( data.path ) {
                window.location = data.path + '?q=' + encodeURIComponent( text )
            }

        }).catch( err => {
            setAsking( false )
            setError( err.message )
        })
    }

    // Shareable and back-button safe, without a reload.
    const rememberQuery = ( text ) => {
        try {
            const url = new URL( window.location.href )
            url.searchParams.set( 'q', text )
            history.replaceState( null, '', url.toString() )
        } catch ( e ) {}
    }

    // Enter inside a form submits it — intercept so it asks instead.
    const onQueryKeyDown = ( e ) => {
        if( e.key === 'Enter' ) {
            e.preventDefault()
            askConcierge()
        }
    }

    ////////////////////
    //  CHIP STATE     //
    ////////////////////

    // Group size and date are permanent pills in concierge mode: filled when
    // set, a dashed prompt when not. Each pill IS its control — a select and a
    // date picker styled as chips — so choosing takes one tap, not a
    // tap-to-reveal then another tap. They read from the form rather than from
    // the last response, so a pill always reflects what is actually applied.
    // Everything else the concierge understood (occasion, city, activities) is
    // already visible in the URL, the results header and the grid's own filter
    // row — repeating it here just showed "Restaurants" twice.
    const dateLabel = () => formData.date && formData.date.isValid && formData.date.isValid()
        ? formData.date.format('D MMM YYYY')
        : null

    const paxLabel = () => {
        const n = parseInt( formData.pax, 10 )
        if( !n ) return null
        return n >= 50 ? '50+ people' : `${n} people`
    }

    // actioned: true — a pill edit applies itself (see the auto-submit effect).
    const answerPax = ( value ) => {
        setFormData({ ...formData, pax: value, actioned: true })
    }

    // react-dates, like quick search, NOT a native date input: desktop Safari's
    // native calendar commits a value just from paging to the next month,
    // which applied a date nobody picked.
    const answerDate = ( date ) => {
        setFormData({ ...formData, date: date, actioned: true })
    }

    const clearSlot = ( slot ) => {
        setFormData({
            ...formData,
            [slot]: slot === 'date' ? null : '',
            actioned: true
        })
    }

    const [ dateFocused, setDateFocused ] = useState( false )
    const [ isMobile, setIsMobile ] = useState( false )

    useEffect(() => {
        setIsMobile( window.matchMedia('(max-width: 768px)').matches )
    }, [])

    // Concierge mode never shows the quick-search selects: the chips carry group
    // size and date, and "Switch to classic filters" is the way back.
    const showFields = !concierge

    // Search is a real POST, so the reload lands wherever the action points. In
    // concierge mode that has to carry the typed query, or the guest comes back to
    // an empty concierge having lost what they wrote. The mount effect reads ?q=
    // and restores both the mode and the text.
    const submitAction = () => {
        if( !concierge || !query.trim() ) return url
        return url + ( url.indexOf('?') === -1 ? '?' : '&' ) + 'q=' + encodeURIComponent( query.trim() )
    }

    return (
        <>
        {/* --tabbed in both modes: the tab row is a full-width line of its own, so
            the band has to wrap even in quick search or the tabs, the hint and the
            three selects all land on one line and the selects collapse. */}
        <form
        	id={FORM_ID}
        	className={`enquiry__form ${type} enquiry__form--tabbed${concierge ? ' enquiry__form--concierge' : ''}`}
        	method="post"
        	action={submitAction()}
        	ref={formRef}
        >

			<div className="concierge__tabs" role="tablist">
				<button
					type="button"
					role="tab"
					aria-selected={!concierge}
					className={`concierge__tab${!concierge ? ' is-active' : ''}`}
					onClick={() => chooseMode( false )}
				>Quick search
				</button>
				<button
					type="button"
					role="tab"
					aria-selected={concierge}
					className={`concierge__tab concierge__tab--ai${concierge ? ' is-active' : ''}`}
					onClick={() => chooseMode( true )}
				>
					<span className="concierge__sparkle" aria-hidden="true">✦</span> AI Concierge
					{!concierge && <span className="concierge__badge">Click to try</span>}
				</button>
				{/* The design uses different copy per mode: a nudge with a worked
				    example while in quick search, reassurance once in AI mode. */}
				{concierge ? (
					<span className="concierge__tabs--hint">
						Describe your party in your own words — we'll do the rest
					</span>
				) : (
					<button
						type="button"
						className="concierge__tabs--try"
						onClick={() => { chooseMode( true ); setQuery( EXAMPLE_QUERY ) }}
					>Or try: “{EXAMPLE_QUERY}” →
					</button>
				)}
			</div>

			{concierge &&
				<div className="concierge__ask">
					<label className="show-for-sr" htmlFor="concierge-query">
						Describe your party
					</label>
					<div className="concierge__ask--field">
						<span className="concierge__sparkle" aria-hidden="true">✦</span>
						<textarea
							id="concierge-query"
							className="concierge__ask--input"
							rows="1"
							autoComplete="off"
							placeholder={askPlaceholder || `e.g. ${EXAMPLE_QUERY}`}
							value={query}
							onChange={e => setQuery( e.target.value )}
							onKeyDown={onQueryKeyDown}
							disabled={asking}
						/>
					</div>
					{/* type=button: this must not submit the enclosing form.
					    The modal variant asks from its footer CTA instead. */}
					{!modal &&
						<button
							className="concierge__ask--button"
							type="button"
							onClick={askConcierge}
							disabled={asking || !query.trim()}
						>{asking ? 'Asking…' : 'Ask'}
						</button>
					}
				</div>
			}

			{concierge && vibes &&
				<div className="concierge__vibes">
					{VIBES.map( v => (
						<button key={v} type="button" onClick={() => addVibe( v )}>{v}</button>
					))}
				</div>
			}

           {( sections.length > 0 || activities.length > 0 ) &&
				<div className={`enquiry__form--type${showFields ? '' : ' hide'}`}>
					<select
						aria-label="Party or activity type"
						value={searchType}
						onChange={e => chooseSearchType( e.target.value )}
					>
						<option value="">Choose Party or Activity Type</option>
						{sections.length > 0 &&
							<optgroup label="Party Type">
								{sections.map( s => (
									<option key={s.slug} value={`p:${s.slug}`}>{s.name}</option>
								))}
							</optgroup>
						}
						{activities.length > 0 &&
							<optgroup label="Activity Type">
								{activities.map( a => (
									<option key={a.slug} value={`a:${a.slug}`}>{a.name}</option>
								))}
							</optgroup>
						}
					</select>
				</div>
			}

           {/* In concierge mode the design replaces the selects with the freeform
               input; a dashed "+ How many people?" chip reveals them again, and so
               does "Switch to classic filters". Nothing is ever lost either way. */}
           <div className={`enquiry__form--date--pax${showFields ? '' : ' hide'}`}>
				<PaxField
					formData={formData}
					setFormData={setFormData}
					labels={labels}
				/>

				<DateField
					date={date}
					setDate={setDate}
					formData={formData}
					setFormData={setFormData}
					labels={labels}
				/>
            </div>

            { hideLocation && formData.location != undefined && formData.location != null && formData.location != '' ? (
            	<input type="hidden"  name="enquiry[location]" value={formData.location} />
            ) : (
            	<div className={`enquiry__form--location-holder${showFields ? '' : ' hide'}`}>
					<LocationField
						config={fieldConfig}
						location={location}
						setLocation={setLocation}
						formData={formData}
						setFormData={setFormData}
						setUrl={setUrl}
						genPath={genPath}
						labels={labels}
					/>
					{chipList().length > 0 &&
						<div className="enquiry__form--cities">
							{chipList().map( l => (
								<button
									key={l.slug}
									type="button"
									className={`enquiry__form--city${formData.location === l.slug ? ' is-active' : ''}`}
									onClick={() => pickCity( l.slug )}
								>{l.name}
								</button>
							))}
						</div>
					}
				</div>
			)}

            {modal ? (
				<div className={`enquiry__form--footer${concierge ? ' hide' : ''}`}>
					<button
						className="button primary enquiry__form--cta"
						type="submit"
						disabled={!canSubmit()}
					>{ctaLabel || 'Search'}
					</button>
				</div>
            ) : (
				<div className={`enquiry__form--button${concierge ? ' hide' : ''}`}>
					<button
						className="button primary expanded"
						type="submit"
						disabled={!canSubmit()}
					>{ctaLabel || 'Search'}
					</button>
				</div>
			)}

            {modal && concierge &&
				<div className="enquiry__form--footer">
					<button
						type="button"
						className="enquiry__form--browse"
						onClick={() => chooseMode( false )}
					>Switch to classic filters
					</button>
					<button
						type="button"
						className="button primary enquiry__form--cta"
						onClick={askConcierge}
						disabled={asking || !query.trim()}
					>{asking ? 'Asking…' : '✦ Ask the concierge'}
					</button>
				</div>
			}

            <input name="utf8" type="hidden" value="&#x2713;" />
            <input name="authenticity_token" value={config.token} type="hidden" />
        </form>

        {/* Group size and date are always here in concierge mode, filled or not —
            they are the two filters the grid's own row doesn't show, and a pill
            that vanishes when cleared looked like a bug. Everything else the
            concierge applied already shows in the grid's filter row. The category
            modal shows vibe starters instead: nothing has been searched
            yet, so there is nothing to refine. */}
        {concierge && !modal &&
            <div className="concierge__chips">
                <span className={`concierge__chip--pill${paxLabel() ? '' : ' is-empty'}`}>
                    <select
                        aria-label="How many people?"
                        value={formData.pax || ''}
                        onChange={e => answerPax( e.target.value )}
                    >
                        <option value="">+ How many people?</option>
                        {[...Array(49).keys()].map( i => (
                            <option key={i + 1} value={i + 1}>{i + 1} people</option>
                        ))}
                        <option value="50">50+ people</option>
                    </select>
                    {paxLabel() &&
                        <button
                            type="button"
                            className="concierge__chip--x"
                            aria-label="Remove group size"
                            onClick={() => clearSlot( 'pax' )}
                        >✕
                        </button>
                    }
                </span>

                <span className={`concierge__chip--pill concierge__chip--date${dateLabel() ? '' : ' is-empty'}`}>
                    <SingleDatePicker
                        id="concierge-date"
                        date={dateLabel() ? formData.date : null}
                        onDateChange={answerDate}
                        focused={dateFocused}
                        onFocusChange={({ focused }) => setDateFocused( focused )}
                        numberOfMonths={1}
                        hideKeyboardShortcutsPanel={true}
                        readOnly={true}
                        displayFormat="D MMM YYYY"
                        placeholder="+ When?"
                        noBorder={true}
                        withFullScreenPortal={isMobile}
                    />
                    {dateLabel() &&
                        <button
                            type="button"
                            className="concierge__chip--x"
                            aria-label="Remove date"
                            onClick={() => clearSlot( 'date' )}
                        >✕
                        </button>
                    }
                </span>

                <button
                    type="button"
                    className="concierge__chips--classic"
                    onClick={() => chooseMode( false )}
                >Switch to classic filters
                </button>
            </div>
        }

        {concierge && ( reply || error ) &&
            <div className="concierge__summary" aria-live="polite">
                {reply &&
                    <p>
                        <strong>
                            <span className="concierge__sparkle" aria-hidden="true">✦</span> Your concierge says:
                        </strong>{' '}{reply}
                    </p>
                }
                {/* No per-pair distance list. It sat between the guest and the
                    results, and every card now carries its own distance while the
                    grid is narrowed to the area — so the list restated what was
                    already on screen, at length. */}
                {error && <p className="concierge__summary--error">{error}</p>}
            </div>
        }
        </>
    )
}

const PaxField = ({ formData, setFormData, labels }) => {

    const paxList = [...Array(49).keys()].map(x => x+=1)
    paxList.push('50+')

    return (
        <div className="enquiry__form--pax">
            {labels && <span className="enquiry__form--label">Group size</span>}
            <select
                name="enquiry[pax]" 
                value={formData.pax} 
                onChange={(e) => setFormData( {...formData, pax: e.target.value, actioned: true })}
            >
                <option value="" key="pax-0">Group Size</option>
                {paxList.map((p, index) => {
                	const value = p == '50+' ? 50 : p
                    return (
                        <option key={`pax-` + p} value={value}>{p} people</option>
                    )
                })}
            </select>
        </div>
    )
}

const DateField = ({ formData, setFormData, labels }) => {

    const [ focused, setFocused ] = useState( false )
    const [ isMobile, setIsMobile ] = useState( false )

    useEffect(() => {
		setIsMobile( window.matchMedia("(max-width: 768px)").matches )
	}, []);

    return (
        <div className="enquiry__form--date">
            {labels && <span className="enquiry__form--label">Date <em>(optional)</em></span>}
            <SingleDatePicker
                id="enquiry[date]"
                date={formData.date}
                onDateChange={date => setFormData({...formData, date: date, actioned: true })}
                onFocusChange={({ focused }) => {
                    setFocused( focused )
                }}
                focused={focused}
                showDefaultInputIcon={true}
                inputIconPosition="before"
                numberOfMonths={1}
                hideKeyboardShortcutsPanel={true}
                readOnly={true}
                displayFormat="DD/MM/YYYY"
                withFullScreenPortal={isMobile}
                disabled={false}
                keepOpenOnDateSelect={false}
            />
        </div>
    )
}

const LocationField = ({ config, formData, setFormData, setUrl, genPath, labels }) => {
    
    const cityList = () => {
    	return (
    		<>
    		{config.locations.map((location, index) => (
    			<option value={location.slug} key={location.id}>{location.name}</option>
            ))}
            </>
    	)
    }
    
    
    const regionList = () => {
    	return (
    		<>
    		<optgroup label="UK cities">
    		{config.locations.uk.map((location, index) => (
    			<option value={location.slug} key={location.id}>{location.name}</option>
            ))}
            </optgroup>
            <optgroup label="European cities">
    		{config.locations.eu.map((location, index) => (
    			<option value={location.slug} key={location.id}>{location.name}</option>
            ))}
            </optgroup>
            </>
    	)
    }
    
    
    return (
        <div className="enquiry__form--location">
            {labels && <span className="enquiry__form--label">Where</span>}
            <select
                value={formData.location} 
                name="enquiry[location]" 
                onChange={(e) => {
                    setFormData({...formData, location: e.target.value, actioned: true })
                    setUrl( genPath( e.target.value  ) )
                }}
            >
                <option value="">Select City</option>
                
                {config.hasRegion ? regionList() : cityList()}
            </select>
        </div>
    )
}

export default EnquiryForm
